/**
 * VerifyCblStep — Step 2 of the Add-Building modal.
 *
 * Read-only. Consumes the selected `CblPreviewMatch` from Step 1 and
 * surfaces one of three outcome panels:
 *
 *   - `on_2026_cbl`   — green banner, preview law/deadline/fee from the
 *                       filingContext[]; primary CTA "Continue"
 *   - `delisted`      — info banner + dispute guidance; two CTAs:
 *                       "File a CBL dispute" (primary) and "Continue
 *                       without filing" (secondary)
 *   - `never_on_cbl`  — info banner; CTA "Continue (mark as not subject)"
 *
 * When the preview endpoint is degraded (fallback path), `filingContexts[]`
 * is empty — we render a short copy explaining the preview isn't available
 * rather than blocking the step.
 *
 * Sprint 7 § D1 — Soft-confirm recovery banner:
 * When the user picked from a multi-candidate set (`wasAmbiguous === true`),
 * we render an amber/info banner ABOVE the outcome panel that surfaces the
 * picked address + BIN and offers a "Wrong building?" link back to step 1.
 * The banner is informational, NOT an error — it acknowledges the user's
 * choice and offers an escape hatch in case they picked the wrong row.
 * Hidden when the resolver had a single match (auto-advance / single-match
 * BBL clicks) — there's nothing to second-guess in that case.
 */

import React, { useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, Info, ShieldCheck } from "lucide-react";

import { Button } from "@/components/shared/ui/button";
import {
  formatDeadline,
  formatFeeCents,
  formatLaw,
} from "@/utils/filingContextFormat";
import type { FilingContext } from "@/api/filingContext";
import type { CblPreviewMatch } from "./types";

/**
 * Props the inner outcome panels need. Kept as the original 4-field shape
 * so the panels don't (falsely) declare D1 props they never read — the
 * recovery banner is the top-level step's concern, not the panels'.
 */
interface VerifyCblPanelCoreProps {
  match: CblPreviewMatch;
  onBack: () => void;
  onContinue: () => void;
  onClose: () => void;
}

interface VerifyCblStepProps extends VerifyCblPanelCoreProps {
  /**
   * Sprint 7 § D1 — true when the user picked this match from a list of
   * >1 candidates. Drives the "You picked X from N buildings" recovery
   * banner. False for auto-advance / single-match paths.
   */
  wasAmbiguous?: boolean;
  /**
   * Sprint 7 § D1 — the candidate count at the moment of selection. Used
   * for banner copy ("from {N} buildings at this BBL"). When > 25 we
   * soften the copy so it doesn't read as an apology. 0 / 1 means we
   * shouldn't be rendering the banner at all (guard via `wasAmbiguous`).
   */
  originalCandidateCount?: number;
  /**
   * Sprint 7 § D1 — fired when the user clicks "Wrong building?" in the
   * recovery banner. The dialog dispatches `CLEAR_MATCH`, which routes
   * back to step 1 with `searchTerm` preserved. Distinct from `onBack`
   * (the panel's "Back" button does the same thing today, but D1 keeps
   * the affordances logically separate so future changes to either path
   * don't bleed into the other).
   */
  onRestartSearch?: () => void;
}

type PanelBaseProps = Omit<VerifyCblPanelCoreProps, "onClose">;

const IdentityBlock: React.FC<{ match: CblPreviewMatch }> = ({ match }) => (
  <div>
    <div className="font-semibold">
      {match.identity.address ?? "(no address on file)"}
    </div>
    <div className="text-xs text-muted-foreground mt-0.5">
      {match.identity.bbl && <>BBL {match.identity.bbl}</>}
      {match.identity.bbl && match.identity.bin && " · "}
      {match.identity.bin && <>BIN {match.identity.bin}</>}
    </div>
  </div>
);

const PATHWAYS_MAP: Record<string, string> = {
  CP0: "Article 320 (2024)",
  CP1: "Article 320 (2026)",
  CP2: "Article 320 (2035)",
  CP3: "Article 321",
  CP4: "City Portfolio",
};

const FilingLine: React.FC<{ fc: FilingContext }> = ({ fc }) => {
  const law = formatLaw(fc.law);
  const deadline = formatDeadline(fc.cycle.standardDeadline);
  const fee = formatFeeCents(fc.law.dobPaymentFeeCents ?? null);
  const pathwayLabel = fc.previewPathway
    ? (PATHWAYS_MAP[fc.previewPathway] ?? fc.previewPathway)
    : null;
  return (
    <div className="text-sm">
      <span className="font-medium">{law}</span>
      {fc.law.compliancePeriodLabel ? ` — ${fc.law.compliancePeriodLabel}` : ""}
      {deadline && (
        <>
          <br />
          <span className="text-muted-foreground">
            Deadline {deadline}
            {fee ? ` · DOB fee ${fee}` : ""}
          </span>
        </>
      )}
      {pathwayLabel && (
        <>
          <br />
          <span className="text-muted-foreground">Pathway: {pathwayLabel}</span>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Recovery banner (D1) — soft confirmation for ambiguous picks
// ---------------------------------------------------------------------------

/**
 * Threshold above which we soften the copy. With 26+ candidates at one BBL
 * (think: a co-op campus, a hospital complex), reading "you picked X from
 * 47 buildings" sounds vaguely accusatory. The softer phrasing leads with
 * the count as context, not as a warning. 25 is informed by the cardinality
 * research — see outreach-research/memory/24_research_onboarding_cardinality_2026-04-30.md
 * for the BBL-fanout distribution.
 */
const HIGH_CARDINALITY_THRESHOLD = 25;

interface RecoveryBannerProps {
  match: CblPreviewMatch;
  originalCandidateCount: number;
  onRestart: () => void;
}

const RecoveryBanner: React.FC<RecoveryBannerProps> = ({
  match,
  originalCandidateCount,
  onRestart,
}) => {
  const address = match.identity.address ?? "(no address on file)";
  const { bin } = match.identity;
  const isHighCardinality = originalCandidateCount > HIGH_CARDINALITY_THRESHOLD;
  return (
    <div
      // role="status" matches the outcome panels below — informational, not
      // an error. Polite live-region announcement is fine; users who land
      // here from auto-advance won't hear it because it's not rendered.
      role="status"
      data-testid="recovery-banner"
      className="rounded-md border border-warning/30 bg-warning/10 p-4 flex flex-col sm:flex-row gap-3 sm:items-start"
    >
      <Info
        className="h-5 w-5 shrink-0 mt-0.5"
        aria-hidden="true"
        style={{ color: "hsl(var(--warning))" }}
      />
      <div className="flex-1 text-sm">
        {isHighCardinality ? (
          <>
            We found {originalCandidateCount} buildings at this BBL — you picked{" "}
            <span className="font-medium">{address}</span>
            {bin && (
              <>
                , BIN <span className="font-mono tabular-nums">{bin}</span>
              </>
            )}
            . Wrong building?
          </>
        ) : (
          <>
            You picked <span className="font-medium">{address}</span>
            {bin && (
              <>
                , BIN <span className="font-mono tabular-nums">{bin}</span>
              </>
            )}{" "}
            from {originalCandidateCount} buildings at this BBL.
          </>
        )}
      </div>
      <Button
        type="button"
        variant="link"
        className="h-auto p-0 self-start sm:self-center whitespace-nowrap"
        onClick={onRestart}
      >
        Wrong building? Go back
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// On-CBL panel
// ---------------------------------------------------------------------------

const OnCblPanel: React.FC<PanelBaseProps> = ({
  match,
  onBack,
  onContinue,
}) => {
  const filingContexts = match.filingContexts ?? [];
  return (
    <>
      <div
        role="status"
        className="rounded-md border border-success/30 bg-success/10 p-4 flex gap-3"
      >
        <ShieldCheck
          className="h-5 w-5 shrink-0 mt-0.5"
          aria-hidden="true"
          style={{ color: "hsl(var(--success))" }}
        />
        <div className="flex-1">
          <IdentityBlock match={match} />
          <div className="mt-3 font-semibold">
            On the 2026 NYC Covered Buildings List
          </div>
          {filingContexts.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground mt-2">
                You&apos;re required to file:
              </p>
              <div className="mt-2 flex flex-col gap-2">
                {filingContexts.map((fc) => (
                  <FilingLine
                    key={`${fc.law.code}-${fc.cycle.calendarYear}`}
                    fc={fc}
                  />
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground mt-2">
              We&apos;ll pull the law, deadline, and fee preview from NYC as
              soon as the building is added.
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-row justify-between gap-3 pt-4">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button type="button" onClick={onContinue}>
          Continue
        </Button>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Delisted panel
// ---------------------------------------------------------------------------

const DelistedPanel: React.FC<VerifyCblPanelCoreProps> = ({
  match,
  onBack,
  onContinue,
  onClose,
}) => {
  const navigate = useNavigate();
  const handleDispute = () => {
    onClose();
    // The dispute flow takes an LL97SubmissionId today; a cycle-zero dispute
    // from the creation surface isn't wired yet. Route the user to the
    // disputes index so they can open a ticket. Flag this as a backend-gap
    // follow-up in the report.
    navigate({ to: "/cbl_disputes" });
  };
  return (
    <>
      <div
        role="status"
        className="rounded-md border border-info/30 bg-info/10 p-4 flex gap-3"
      >
        <Info
          className="h-5 w-5 shrink-0 mt-0.5"
          aria-hidden="true"
          style={{ color: "hsl(var(--info))" }}
        />
        <div className="flex-1">
          <IdentityBlock match={match} />
          <div className="mt-3 font-semibold">
            Your building was removed from the 2026 Covered Buildings List
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Buildings not on the 2026 CBL are not required to file LL97 for
            CY2025 or going forward. You won&apos;t be penalized for not filing.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Common reasons: demolished, de-energized &gt;12 months,
            reclassified, or reduced below 25,000 sq ft. If you believe this is
            wrong, you can file a CBL dispute — NYC DOB must respond within 30
            days.
          </p>
        </div>
      </div>
      <div className="flex flex-row flex-wrap justify-between gap-3 pt-4">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <div className="flex flex-row gap-3">
          <Button type="button" variant="outline" onClick={onContinue}>
            Continue without filing
          </Button>
          <Button type="button" onClick={handleDispute}>
            File a CBL dispute
          </Button>
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Never-on-CBL panel
// ---------------------------------------------------------------------------

const NeverOnCblPanel: React.FC<PanelBaseProps> = ({
  match,
  onBack,
  onContinue,
}) => (
  <>
    <div
      role="status"
      className="rounded-md border border-info/30 bg-info/10 p-4 flex gap-3"
    >
      <AlertCircle
        className="h-5 w-5 shrink-0 mt-0.5"
        aria-hidden="true"
        style={{ color: "hsl(var(--info))" }}
      />
      <div className="flex-1">
        <IdentityBlock match={match} />
        <div className="mt-3 font-semibold">
          This building doesn&apos;t appear on NYC&apos;s 2026 Covered Buildings
          List
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          That usually means the building is under 25,000 sq ft, owned by an
          exempt entity, or not yet classified. You can still add it to your
          portfolio — it will be flagged as not subject to LL97/LL88 until NYC
          adds it to a future CBL.
        </p>
      </div>
    </div>
    <div className="flex flex-row justify-between gap-3 pt-4">
      <Button type="button" variant="ghost" onClick={onBack}>
        Back
      </Button>
      <Button type="button" onClick={onContinue}>
        Continue (mark as not subject)
      </Button>
    </div>
  </>
);

// ---------------------------------------------------------------------------
// Top-level step
// ---------------------------------------------------------------------------

const VerifyCblStep: React.FC<VerifyCblStepProps> = ({
  match,
  onBack,
  onContinue,
  onClose,
  wasAmbiguous,
  originalCandidateCount = 0,
  onRestartSearch,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // The banner only renders when the user actually had a choice. We also
  // require `onRestartSearch` to be wired — without it the "Wrong building?"
  // link would be a dead end, which is worse than not showing the banner.
  const showRecoveryBanner =
    wasAmbiguous === true &&
    originalCandidateCount > 1 &&
    typeof onRestartSearch === "function";

  // Only the core 4 props get forwarded to the outcome panels — the D1
  // props (`wasAmbiguous`, `originalCandidateCount`, `onRestartSearch`)
  // are consumed entirely at this level. Forwarding them to panels would
  // be a false-positive vector for the `react/no-unused-prop-types` rule.
  const panelProps: VerifyCblPanelCoreProps = {
    match,
    onBack,
    onContinue,
    onClose,
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-lg font-semibold leading-tight focus:outline-none"
        >
          Verify with NYC
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Here&apos;s what NYC&apos;s records say about this building. Confirm
          to continue.
        </p>
      </div>
      {showRecoveryBanner && (
        <RecoveryBanner
          match={match}
          originalCandidateCount={originalCandidateCount}
          onRestart={onRestartSearch as () => void}
        />
      )}
      {match.outcome === "on_2026_cbl" && <OnCblPanel {...panelProps} />}
      {match.outcome === "delisted" && <DelistedPanel {...panelProps} />}
      {match.outcome === "never_on_cbl" && <NeverOnCblPanel {...panelProps} />}
    </div>
  );
};

export default VerifyCblStep;
